/* eslint-disable max-statements */
import { Box, Icon, Input, Label, Text } from '@nimbus-ds/components';
import { ExclamationTriangleIcon } from '@nimbus-ds/icons';
import { InterfaceNameValue, RadioButtonGroup } from '@tiendanube/components';
import { ProductCountOptionsEnum } from 'domains/Orders/Orders/ordersService/constants';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';

export interface ProductUnitsCountFilterValue {
  selectedOption: ProductCountOptionsEnum;
  minUnits: string;
  maxUnits: string;
}

interface ProductUnitsCountFilterProps {
  selectedOption: ProductCountOptionsEnum;
  minUnits: string;
  maxUnits: string;
  showErrors: boolean;
  onChange: (value: ProductUnitsCountFilterValue) => void;
}

export function ProductUnitsCountFilter({
  selectedOption,
  minUnits,
  maxUnits,
  showErrors = false,
  onChange,
}: Readonly<ProductUnitsCountFilterProps>): JSX.Element {
  const t = useTranslationOrders('filter.productsCountFilter');

  const options = [
    {
      label: t('options.all'),
      value: ProductCountOptionsEnum.ALL,
    },
    {
      label: t('options.exactNumber'),
      value: ProductCountOptionsEnum.EXACT_NUMBER,
    },
    {
      label: t('options.range'),
      value: ProductCountOptionsEnum.RANGE,
    },
  ];

  const allowedKeys = ['Backspace', 'ArrowUp', 'ArrowDown', 'Tab'];

  const handleMinValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      selectedOption: ProductCountOptionsEnum.RANGE,
      minUnits: e.target.value,
      maxUnits: maxUnits,
    });
  };

  const handleMaxValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      selectedOption: ProductCountOptionsEnum.RANGE,
      minUnits: minUnits,
      maxUnits: e.target.value,
    });
  };

  const handleExactNumberValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    onChange({
      selectedOption: ProductCountOptionsEnum.EXACT_NUMBER,
      minUnits: e.target.value,
      maxUnits: e.target.value,
    });
  };

  const handleChangeOption = (e: InterfaceNameValue) => {
    onChange({
      selectedOption: e.value as ProductCountOptionsEnum,
      minUnits: '',
      maxUnits: '',
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/\d/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <Box borderStyle="none">
      <Box paddingBottom="2" display="flex" alignItems="center" gap="1">
        <Label>{t('title')}</Label>
      </Box>
      <Box>
        <RadioButtonGroup
          name="productsCountOptionFilter"
          options={options}
          onChange={handleChangeOption}
          value={selectedOption}
        />
      </Box>
      {selectedOption === ProductCountOptionsEnum.EXACT_NUMBER && (
        <Box>
          <Input
            name="exactNumberValue"
            type="number"
            min={1}
            value={minUnits ?? ''}
            placeholder={t('inputPlaceholder')}
            onChange={handleExactNumberValueChange}
            onKeyDown={handleKeyDown}
          />
        </Box>
      )}
      {selectedOption === ProductCountOptionsEnum.RANGE && (
        <Box>
          <Box display="flex" flexDirection="row">
            <Input
              name="minValue"
              type="number"
              min={1}
              value={minUnits ?? ''}
              placeholder={t('inputMinPlaceholder')}
              onChange={handleMinValueChange}
              onKeyDown={handleKeyDown}
              appearance={showErrors ? 'danger' : 'neutral'}
            />
            <Box marginRight="2" marginLeft="2" alignSelf="center">
              <Label>a</Label>
            </Box>
            <Input
              name="maxValue"
              type="number"
              min={1}
              value={maxUnits ?? ''}
              placeholder={t('inputMaxPlaceholder')}
              onChange={handleMaxValueChange}
              onKeyDown={handleKeyDown}
              appearance={showErrors ? 'danger' : 'neutral'}
            />
          </Box>
          {showErrors && (
            <Box display="flex" flexDirection="row" gap="0-5" paddingTop="1">
              <Icon
                color="danger-textLow"
                source={<ExclamationTriangleIcon size="small" />}
              />
              <Text
                as="span"
                color="danger-textLow"
                fontSize="caption"
                lineHeight="caption"
              >
                {t('errorCaption')}
              </Text>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
